import { useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Spinner } from "@phosphor-icons/react";
import { toast } from "sonner";
import VoiceInput from "@/components/VoiceInput";
import { StarInput } from "@/components/reviews/StarRating";
import ReviewPhotos from "@/components/reviews/ReviewPhotos";
import ProductPicker from "@/components/reviews/ProductPicker";
import { useCustomer } from "@/context/CustomerContext";

const Field = ({ label, children }) => (
  <div className="space-y-2"><Label className="text-xs uppercase tracking-widest">{label}</Label>{children}</div>
);

export default function ReviewForm({ product: fixedProduct, prefill: given, onDone }) {
  const { customer } = useCustomer();
  const prefill = given || (customer ? { name: customer.name, email: customer.email } : null);
  const [product, setProduct] = useState(fixedProduct || null);
  const [f, setF] = useState(() => ({ name: prefill?.name || "", email: prefill?.email || "", title: "", text: "", website: "" }));
  const [rating, setRating] = useState(0);
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState("");
  const [openedAt] = useState(() => new Date().toISOString());
  const set = (k) => (e) => setF({ ...f, [k]: e.target.value });

  async function submit(e) {
    e.preventDefault();
    if (!product) return toast.error("Pick the item you're reviewing.");
    if (!rating) return toast.error("Tap the stars to give a rating.");
    try {
      const photo_paths = [];
      for (let i = 0; i < photos.length; i++) {
        setBusy(`Uploading photo ${i + 1} of ${photos.length}…`);
        const fd = new FormData();
        fd.append("file", photos[i].file);
        const up = await api.post("/uploads/photo", fd, { headers: { "Content-Type": "multipart/form-data" } });
        photo_paths.push(up.data.path);
      }
      setBusy("Submitting…");
      const r = await api.post("/reviews", {
        product_id: product.id, rating, photo_paths, form_opened_at: openedAt,
        name: f.name.trim(), email: f.email.trim(), title: f.title.trim() || null, text: f.text.trim(), website: f.website,
      });
      toast.success(r.data.message);
      onDone?.();
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setBusy("");
    }
  }

  return (
    <DialogContent data-testid="review-dialog" className="rounded-sm max-w-lg max-h-[92vh] overflow-y-auto">
      <DialogHeader>
        <DialogTitle className="font-display text-2xl">Write a review</DialogTitle>
        <DialogDescription>Tell fellow anglers how it fits, feels and gets laughs. Add up to 3 photos.</DialogDescription>
      </DialogHeader>
      <form onSubmit={submit} className="space-y-4">
        <input aria-hidden="true" tabIndex={-1} autoComplete="off" value={f.website} onChange={set("website")}
          style={{ position: "absolute", left: "-9999px", width: 1, height: 1 }} />
        <Field label="Item">
          {fixedProduct ? <div className="text-sm font-display" data-testid="review-product-fixed">{fixedProduct.title}</div>
            : <ProductPicker value={product} onChange={setProduct} />}
        </Field>
        <Field label="Your rating"><StarInput value={rating} onChange={setRating} /></Field>
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
          <Field label="Name"><Input data-testid="review-name" value={f.name} onChange={set("name")} maxLength={60} required placeholder="Captain Mike" className="rounded-sm" /></Field>
          <Field label="Email (private)"><Input data-testid="review-email" type="email" value={f.email} onChange={set("email")} required placeholder="you@email.com" className="rounded-sm" /></Field>
        </div>
        <Field label="Headline (optional)"><Input data-testid="review-title" value={f.title} onChange={set("title")} maxLength={100} placeholder="Got laughs at the boat launch" className="rounded-sm" /></Field>
        <Field label="Your review">
          <Textarea data-testid="review-text" value={f.text} onChange={set("text")} rows={4} maxLength={1500} required minLength={4}
            placeholder="Fits true to size, the print hasn't faded after 10 washes…" className="rounded-sm resize-none" />
          <div className="flex items-center justify-between gap-3">
            <VoiceInput testIdPrefix="review-voice" label="Dictate your review"
              onTranscript={(t) => setF((p) => ({ ...p, text: (p.text ? `${p.text.trim()} ${t}` : t).slice(0, 1500) }))} />
            <span className="text-xs text-muted-foreground">{f.text.length}/1500</span>
          </div>
        </Field>
        <Field label="Photos of your item (optional)"><ReviewPhotos files={photos} setFiles={setPhotos} /></Field>
        <Button type="submit" data-testid="review-submit" disabled={!!busy} className="w-full btn-primary rounded-sm h-11 font-display tracking-wide">
          {busy && <Spinner size={16} className="mr-2 animate-spin" />}{busy || "Submit review"}
        </Button>
        <p className="text-xs text-muted-foreground text-center">Reviews appear after approval. Your email is never shown publicly.</p>
      </form>
    </DialogContent>
  );
}
